'use client';
import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Clock, Zap, Search, ArrowRight, AlertTriangle, ExternalLink, Send, Calendar, Rocket, Star, TrendingUp } from 'lucide-react';
import { logEvent, triggerAutomatedEmail } from '@/lib/analytics/eventLogger';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import ClaimPreMessageModal from '@/components/ClaimPreMessageModal';
import AirdropDetailPage from './AirdropDetailPage';

// ─── Rocket Launchpad (same as homepage) ────────────────────────────────────

const SCALE_MILESTONES = ['2X', '5X', '10X', '50X', '100X', '500X', '1000X', '5000X'];
const MILESTONE_COLORS = [
  '#22C55E', '#84CC16', '#EAB308', '#F97316',
  '#EC4899', '#A855F7', '#3B82F6', '#EF4444',
];

interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  life: number;
}

function RocketLaunchpad({ rocketName }: { rocketName: string }) {
  const [phase, setPhase] = useState<'countdown' | 'launching' | 'flying' | 'celebrating'>('countdown');
  const [countdown, setCountdown] = useState(3);
  const [rocketY, setRocketY] = useState(0);
  const [currentMilestone, setCurrentMilestone] = useState(0);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [flameSize, setFlameSize] = useState(1);
  const animRef = useRef<number | null>(null);
  const milestoneRef = useRef<NodeJS.Timeout | null>(null);
  const particleIdRef = useRef(0);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 900);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => setPhase('launching'), 400);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  useEffect(() => {
    if (phase === 'launching') {
      const t = setTimeout(() => setPhase('flying'), 600);
      return () => clearTimeout(t);
    }
  }, [phase]);

  // Milestone updates are driven by the rocketY animation loop directly

  useEffect(() => {
    if (phase !== 'flying') return;
    let start: number | null = null;
    const duration = 7000;
    const maxY = 230;
    const animate = (ts: number) => {
      if (!start) start = ts;
      const elapsed = ts - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress;
      setRocketY(eased * maxY);
      setFlameSize(1 + eased * 1.5);
      const milestoneIndex = Math.min(
        Math.floor(progress * SCALE_MILESTONES.length),
        SCALE_MILESTONES.length - 1
      );
      setCurrentMilestone(milestoneIndex);
      if (progress < 1) {
        animRef.current = requestAnimationFrame(animate);
      } else {
        setPhase('celebrating');
        setTimeout(() => {
          setRocketY(0);
          setCurrentMilestone(0);
          setPhase('countdown');
          setCountdown(3);
          start = null;
        }, 2000);
      }
    };
    animRef.current = requestAnimationFrame(animate);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [phase]);

  const rocketYRef = useRef(0);
  useEffect(() => { rocketYRef.current = rocketY; }, [rocketY]);

  useEffect(() => {
    if (phase !== 'flying') return;
    const interval = setInterval(() => {
      const color = MILESTONE_COLORS[Math.floor(Math.random() * MILESTONE_COLORS.length)];
      const newParticles: Particle[] = Array.from({ length: 3 }, () => ({
        id: particleIdRef.current++,
        x: 50 + (Math.random() - 0.5) * 20,
        y: 100 - (rocketYRef.current / 230) * 80,
        vx: (Math.random() - 0.5) * 3,
        vy: Math.random() * 2 + 1,
        color,
        size: Math.random() * 4 + 2,
        life: 1,
      }));
      setParticles(prev => [...prev.slice(-30), ...newParticles]);
    }, 80);
    return () => clearInterval(interval);
  }, [phase]);

  useEffect(() => {
    const interval = setInterval(() => {
      setParticles(prev =>
        prev
          .map(p => ({ ...p, life: p.life - 0.06, y: p.y + p.vy, x: p.x + p.vx }))
          .filter(p => p.life > 0)
      );
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const currentColor = MILESTONE_COLORS[currentMilestone % MILESTONE_COLORS.length];
  const progressPct = (rocketY / 230) * 100;
  const isFlying = phase === 'flying' || phase === 'celebrating';

  return (
    <div className="relative flex flex-col items-center select-none w-72">
      <div className="mb-3 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-2"
          style={{
            background: 'linear-gradient(90deg, rgba(249,115,22,0.15), rgba(236,72,153,0.15))',
            border: '1.5px solid rgba(249,115,22,0.35)',
          }}>
          <Rocket size={13} className="text-orange-500" />
          <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">Airdrop Launchpad</span>
        </div>
        <p className="text-xs text-gray-500 font-medium">Watch your token soar 🚀</p>
      </div>

      <div className="relative w-64 h-80 flex flex-col items-center justify-end overflow-hidden rounded-2xl"
        style={{
          background: 'linear-gradient(to bottom, #FFF7ED 0%, #FFF0F6 25%, #F0FFF4 55%, #FFF7ED 80%, #FFFDE7 100%)',
          border: '2px solid rgba(251,146,60,0.25)',
          boxShadow: '0 8px 32px rgba(249,115,22,0.12), 0 2px 8px rgba(236,72,153,0.08)',
        }}>

        {[...Array(16)].map((_, i) => (
          <div key={i} className="absolute rounded-full"
            style={{
              width: i % 4 === 0 ? 5 : i % 3 === 0 ? 4 : 3,
              height: i % 4 === 0 ? 5 : i % 3 === 0 ? 4 : 3,
              background: MILESTONE_COLORS[i % MILESTONE_COLORS.length],
              left: `${8 + (i * 6) % 84}%`,
              top: `${4 + (i * 9) % 55}%`,
              opacity: isFlying ? 0.8 : 0.4,
              animation: `pulse ${1.5 + (i % 3) * 0.5}s ease-in-out infinite`,
              animationDelay: `${i * 0.2}s`,
              transition: 'opacity 0.5s',
            }}
          />
        ))}

        {isFlying && [0, 1, 2].map(i => (
          <div key={`ring-${i}`} className="absolute rounded-full pointer-events-none"
            style={{
              width: 20 + i * 15, height: 20 + i * 15,
              border: `1.5px solid ${MILESTONE_COLORS[(currentMilestone + i) % MILESTONE_COLORS.length]}60`,
              left: `${30 + i * 5}%`, top: `${20 + i * 8}%`,
              animation: 'ping 1.5s ease-out infinite',
              animationDelay: `${i * 0.4}s`, opacity: 0.5,
            }}
          />
        ))}

        {particles.map(p => (
          <div key={p.id} className="absolute rounded-full pointer-events-none"
            style={{
              width: p.size, height: p.size, background: p.color,
              left: `${p.x}%`, top: `${p.y}%`, opacity: p.life,
              transform: 'translate(-50%, -50%)',
              boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
            }}
          />
        ))}

        <div className="absolute right-3 top-4 bottom-16 flex flex-col items-center justify-between z-10">
          {[...SCALE_MILESTONES].reverse().map((scale, i) => {
            const idx = SCALE_MILESTONES.length - 1 - i;
            const isActive = currentMilestone >= idx && isFlying;
            const isCurrent = currentMilestone === idx && isFlying;
            return (
              <div key={scale} className="flex items-center gap-1 transition-all duration-400">
                <div className="rounded-full transition-all duration-300"
                  style={{
                    width: isCurrent ? 8 : 5, height: isCurrent ? 8 : 5,
                    background: isActive ? MILESTONE_COLORS[idx] : '#E5E7EB',
                    boxShadow: isCurrent ? `0 0 8px ${MILESTONE_COLORS[idx]}, 0 0 16px ${MILESTONE_COLORS[idx]}60` : 'none',
                    transform: isCurrent ? 'scale(1.4)' : 'scale(1)',
                  }}
                />
                <span className="font-bold transition-all duration-300"
                  style={{
                    fontSize: isCurrent ? '11px' : '9px',
                    color: isActive ? MILESTONE_COLORS[idx] : '#9CA3AF',
                    textShadow: isCurrent ? `0 0 8px ${MILESTONE_COLORS[idx]}` : 'none',
                  }}>
                  {scale}
                </span>
              </div>
            );
          })}
        </div>

        <div className="absolute left-8 top-4 bottom-16 w-1.5 rounded-full z-10"
          style={{ background: 'rgba(229,231,235,0.6)' }}>
          <div className="absolute bottom-0 left-0 right-0 rounded-full transition-all duration-200"
            style={{
              height: `${progressPct}%`,
              background: `linear-gradient(to top, #22C55E, #EAB308, ${currentColor})`,
              boxShadow: isFlying ? `0 0 10px ${currentColor}80` : 'none',
            }}
          />
        </div>

        {phase === 'countdown' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-30 rounded-2xl"
            style={{ background: 'rgba(255,251,235,0.85)', backdropFilter: 'blur(2px)' }}>
            <div className="text-center">
              <div className="text-6xl font-black mb-2 transition-all duration-300"
                style={{
                  color: countdown > 1 ? '#F97316' : '#EF4444',
                  textShadow: `0 0 20px ${countdown > 1 ? '#F97316' : '#EF4444'}60`,
                  transform: `scale(${1.2 - countdown * 0.05})`,
                }}>
                {countdown > 0 ? countdown : '🚀'}
              </div>
              <p className="text-xs font-bold text-orange-500 uppercase tracking-widest">
                {countdown > 0 ? 'Launching in...' : 'Ignition!'}
              </p>
            </div>
          </div>
        )}

        <div className="absolute z-20 flex flex-col items-center"
          style={{
            bottom: `${52 + rocketY}px`, left: '30px',
            transform: 'translateX(-50%)',
            filter: isFlying ? `drop-shadow(0 0 10px ${currentColor}) drop-shadow(0 0 20px ${currentColor}60)` : 'none',
            transition: 'filter 0.3s',
          }}>
          {isFlying && (
            <div className="flex flex-col items-center">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="rounded-full"
                  style={{
                    width: Math.max(1, (5 - i) * flameSize * 0.8), height: 7 + i, marginTop: 1,
                    background: i === 0 ? '#FDE68A' : i === 1 ? '#FCD34D' : i === 2 ? '#F97316' : i === 3 ? '#EF4444' : 'rgba(239,68,68,0.3)',
                    opacity: 1 - i * 0.15,
                    animation: `pulse ${0.3 + i * 0.1}s ease-in-out infinite`,
                    animationDelay: `${i * 0.05}s`,
                  }}
                />
              ))}
            </div>
          )}
          <div style={{ filter: `drop-shadow(0 2px 4px ${currentColor}40)` }}>
            <div className="w-0 h-0 mx-auto"
              style={{ borderLeft: '9px solid transparent', borderRight: '9px solid transparent', borderBottom: `18px solid ${currentColor}` }} />
            <div className="w-[18px] flex flex-col items-center justify-center relative"
              style={{ height: 32, background: `linear-gradient(135deg, ${currentColor}, ${currentColor}CC)`, borderRadius: '2px', transition: 'background 0.5s' }}>
              <div className="w-3.5 h-3.5 rounded-full bg-white/90 border-2 flex items-center justify-center"
                style={{ borderColor: `${currentColor}80` }}>
                <div className="w-2 h-2 rounded-full" style={{ background: 'radial-gradient(circle, #FDE68A, #F97316)' }} />
              </div>
            </div>
            <div className="flex items-end" style={{ marginTop: -2 }}>
              <div className="w-0 h-0" style={{ borderRight: `7px solid ${currentColor}`, borderTop: '10px solid transparent' }} />
              <div className="w-[18px] h-3" style={{ background: currentColor, borderRadius: '0 0 3px 3px' }} />
              <div className="w-0 h-0" style={{ borderLeft: `7px solid ${currentColor}`, borderTop: '10px solid transparent' }} />
            </div>
          </div>
        </div>

        <div className="relative z-10 w-full flex flex-col items-center" style={{ height: 52 }}>
          {isFlying && (
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-1.5">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="rounded-full"
                  style={{
                    width: 10 + i * 4, height: 10 + i * 4,
                    background: `rgba(251,146,60,${0.3 - i * 0.05})`,
                    animation: `bounce ${0.8 + i * 0.15}s ease-in-out infinite`,
                    animationDelay: `${i * 0.15}s`,
                  }}
                />
              ))}
            </div>
          )}
          <div className="absolute bottom-5 w-24 h-3 rounded-full"
            style={{ background: 'linear-gradient(90deg, #F97316, #EC4899, #EAB308)', boxShadow: '0 3px 12px rgba(249,115,22,0.5)' }} />
          <div className="absolute bottom-0 w-14 h-5 rounded-b-xl"
            style={{ background: 'linear-gradient(180deg, #FB923C, #F97316)' }} />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-10">
            <div className="w-1.5 h-5 rounded-b-sm" style={{ background: '#F97316', transform: 'rotate(-18deg)', transformOrigin: 'top' }} />
            <div className="w-1.5 h-5 rounded-b-sm" style={{ background: '#F97316', transform: 'rotate(18deg)', transformOrigin: 'top' }} />
          </div>
        </div>
      </div>

      <div className="mt-3 w-full px-4 py-3 rounded-2xl text-center transition-all duration-500"
        style={{
          background: `linear-gradient(135deg, ${currentColor}18, ${MILESTONE_COLORS[(currentMilestone + 2) % MILESTONE_COLORS.length]}12)`,
          border: `2px solid ${currentColor}40`,
          boxShadow: isFlying ? `0 0 20px ${currentColor}25, 0 4px 12px rgba(0,0,0,0.06)` : '0 2px 8px rgba(0,0,0,0.06)',
        }}>
        <div className="flex items-center justify-center gap-1.5 mb-1">
          <Rocket size={12} style={{ color: currentColor }} />
          <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Launching</span>
        </div>
        <div className="font-black text-sm mb-1" style={{ color: currentColor, transition: 'color 0.5s' }}>{rocketName}</div>
        <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full font-black text-sm"
          style={{
            background: `${currentColor}20`, color: currentColor,
            border: `1.5px solid ${currentColor}50`,
            animation: isFlying ? 'pulse 0.8s ease-in-out infinite' : 'none',
            transition: 'all 0.5s',
          }}>
          <TrendingUp size={12} />
          {SCALE_MILESTONES[currentMilestone]}
        </div>
      </div>
    </div>
  );
}

// ─── Countdown timer hook ────────────────────────────────────────────────────

function useCountdown(targetDate: string | null | undefined) {
  const [display, setDisplay] = useState('');
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!targetDate) { setDisplay(''); setIsActive(false); return; }
    const calc = () => {
      const diff = new Date(targetDate).getTime() - Date.now();
      if (diff <= 0) { setDisplay(''); setIsActive(false); return; }
      setIsActive(true);
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      if (d > 0) setDisplay(`${d}d ${h}h ${m}m`);
      else if (h > 0) setDisplay(`${h}h ${m}m ${s}s`);
      else setDisplay(`${m}m ${s}s`);
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  return { display, isActive };
}

function computeStatus(drop: AirdropItem): string {
  const now = Date.now();
  let start = drop.start_date ? new Date(drop.start_date).getTime() : null;
  const end = drop.end_date ? new Date(drop.end_date).getTime() : null;
  if (end && now > end) return 'ended';
  if (start && now < start) return 'upcoming';
  if (drop.status === 'verified') return 'verified';
  return drop.status || 'active';
}

interface AirdropItem {
  id: string;
  title: string;
  project_name: string;
  description: string;
  reward_amount: string;
  token_symbol: string;
  category: string;
  status: string;
  image_url: string;
  official_link: string;
  join_url: string;
  telegram_link: string;
  blockchain: string;
  time_left: string;
  total_participants: string;
  participation_type: string;
  is_featured: boolean;
  is_active: boolean;
  sort_order: number;
  start_date?: string;
  end_date?: string;
  daily_volume?: number;
  holder_count?: number;
  countdown_end_at?: string;
  countdown_label?: string;
}

const blockchainFilters = ['All', 'Ethereum', 'Solana', 'Arbitrum', 'Base', 'Polygon', 'Multi-Chain'];
const categoryFilters = ['All', 'DeFi', 'AI', 'DEX', 'RWA', 'DePIN', 'Layer 2', 'GameFi', 'NFT', 'DAO'];

const ITEMS_PER_PAGE = 10;

// ─── Live Token Price Hook ────────────────────────────────────────────────────
function useLiveTokenPrice(symbol: string | undefined) {
  const [price, setPrice] = useState<number | null>(null);
  const [change24h, setChange24h] = useState<number | null>(null);

  useEffect(() => {
    if (!symbol || symbol.length < 2) return;
    let cancelled = false;
    const fetchPrice = async () => {
      try {
        const res = await fetch(`/api/coingecko-price?symbols=${encodeURIComponent(symbol.toLowerCase())}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const entry = data.prices?.[symbol.toLowerCase()];
        if (entry && !cancelled) {
          setPrice(entry.price);
          setChange24h(entry.change24h);
        }
      } catch { /* ignore */ }
    };
    fetchPrice();
    // Refresh every 60s
    const interval = setInterval(fetchPrice, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [symbol]);

  return { price, change24h };
}

function AirdropCard({ drop, onJoinAirdrop, onViewDetail }: { drop: AirdropItem; onJoinAirdrop: (id: string, name: string) => void; onViewDetail: (id: string) => void }) {
  const computedStatus = computeStatus(drop);
  const activeTimer = useCountdown(computedStatus === 'active' || computedStatus === 'verified' ? drop.end_date : null);
  const upcomingTimer = useCountdown(computedStatus === 'upcoming' ? drop.start_date : null);
  // Admin-set countdown clock
  const customCountdown = useCountdown(drop.countdown_end_at || null);
  const { price: livePrice, change24h: liveChange } = useLiveTokenPrice(drop.token_symbol);

  const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
    active: { label: 'Active', color: '#00E5A8', bg: 'rgba(0,229,168,0.1)' },
    upcoming: { label: 'Upcoming', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' },
    ended: { label: 'Ended', color: '#93A4B8', bg: 'rgba(147,164,184,0.1)' },
    verified: { label: 'Verified', color: '#16C2FF', bg: 'rgba(22,194,255,0.1)' },
    unverified: { label: 'Unverified', color: '#93A4B8', bg: 'rgba(147,164,184,0.1)' },
  };
  const status = statusConfig[computedStatus] || statusConfig.ended;
  const symbolChar = drop.token_symbol?.charAt(0) || drop.project_name?.charAt(0) || 'A';
  const showActiveTimer = (computedStatus === 'active' || computedStatus === 'verified') && activeTimer.isActive;
  const showUpcomingTimer = computedStatus === 'upcoming' && upcomingTimer.isActive;
  const showCustomCountdown = drop.countdown_end_at && customCountdown.isActive;

  return (
    <div
      className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.1)] rounded-xl p-5 flex flex-col group hover:border-[rgba(22,194,255,0.25)] transition-all duration-200 cursor-pointer"
      onClick={() => onViewDetail(drop.id)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          {(drop as any).logo_url ? (
            <img
              src={(drop as any).logo_url}
              alt={`${drop.project_name} logo`}
              className="w-11 h-11 rounded-xl object-cover border border-[rgba(22,194,255,0.2)] flex-shrink-0"
            />
          ) : (
            <div className="w-11 h-11 rounded-xl flex items-center justify-center font-sora font-700 text-base flex-shrink-0 bg-[rgba(22,194,255,0.1)] border border-[rgba(22,194,255,0.2)] text-[#16C2FF]">
              {symbolChar}
            </div>
          )}
          <div>
            <div className="font-sora font-600 text-sm text-[#E6F1FF]">{drop.project_name}</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="font-mono text-xs text-[#93A4B8]">{drop.token_symbol}</span>
              {computedStatus === 'verified' && <CheckCircle size={11} className="text-[#00E5A8]" />}
              {/* Live price badge */}
              {livePrice !== null && (
                <span className="flex items-center gap-0.5 text-[10px] font-mono text-[#16C2FF] bg-[rgba(22,194,255,0.08)] px-1.5 py-0.5 rounded-full border border-[rgba(22,194,255,0.15)]">
                  ${livePrice < 0.01 ? livePrice.toFixed(6) : livePrice < 1 ? livePrice.toFixed(4) : livePrice.toFixed(2)}
                  {liveChange !== null && (
                    <span className={`ml-0.5 ${liveChange >= 0 ? 'text-[#00E5A8]' : 'text-[#FF4D6A]'}`}>
                      {liveChange >= 0 ? '▲' : '▼'}{Math.abs(liveChange).toFixed(1)}%
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
        <span className="text-xs font-dm font-medium px-2 py-0.5 rounded-full flex-shrink-0" style={{ color: status.color, background: status.bg }}>
          {status.label}
        </span>
      </div>

      {/* Banner image if available */}
      {(drop as any).banner_url && (
        <div className="mb-3 rounded-lg overflow-hidden" style={{ height: 80 }}>
          <img
            src={(drop as any).banner_url}
            alt={`${drop.project_name} banner`}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <p className="text-xs text-[#93A4B8] font-dm leading-relaxed mb-4 flex-1 line-clamp-3">{drop.description}</p>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-2.5">
          <div className="text-xs text-[#93A4B8] font-dm mb-0.5">Reward</div>
          <div className="font-sora font-700 text-sm text-[#00E5A8]">{(drop as any).reward_amount || '—'}</div>
        </div>
        {/* Admin countdown clock (highest priority) */}
        {showCustomCountdown && (
          <div className="bg-[rgba(139,92,246,0.05)] border border-[rgba(139,92,246,0.2)] rounded-lg p-2.5">
            <div className="text-xs text-[#93A4B8] font-dm mb-0.5">{drop.countdown_label || 'Countdown'}</div>
            <div className="flex items-center gap-1">
              <Clock size={11} className="text-[#A78BFA]" />
              <span className="font-mono text-sm text-[#A78BFA] font-bold">{customCountdown.display}</span>
            </div>
          </div>
        )}
        {!showCustomCountdown && showActiveTimer && (
          <div className="bg-[rgba(0,229,168,0.05)] border border-[rgba(0,229,168,0.15)] rounded-lg p-2.5">
            <div className="text-xs text-[#93A4B8] font-dm mb-0.5">Ends In</div>
            <div className="flex items-center gap-1">
              <Clock size={11} className="text-[#00E5A8]" />
              <span className="font-mono text-sm text-[#00E5A8] font-bold">{activeTimer.display}</span>
            </div>
          </div>
        )}
        {!showCustomCountdown && showUpcomingTimer && (
          <div className="bg-[rgba(245,158,11,0.05)] border border-[rgba(245,158,11,0.15)] rounded-lg p-2.5">
            <div className="text-xs text-[#93A4B8] font-dm mb-0.5">Starts In</div>
            <div className="flex items-center gap-1">
              <Clock size={11} className="text-[#F59E0B]" />
              <span className="font-mono text-sm text-[#F59E0B] font-bold">{upcomingTimer.display}</span>
            </div>
          </div>
        )}
        {computedStatus === 'upcoming' && drop.start_date && !showUpcomingTimer && !showCustomCountdown && (
          <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-2.5">
            <div className="text-xs text-[#93A4B8] font-dm mb-0.5">Launch Date</div>
            <div className="flex items-center gap-1">
              <Calendar size={11} className="text-[#F59E0B]" />
              <span className="font-dm text-xs text-[#F59E0B]">
                {new Date(drop.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          </div>
        )}
        {!showActiveTimer && !showUpcomingTimer && !showCustomCountdown && computedStatus !== 'upcoming' && (
          <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-2.5">
            <div className="text-xs text-[#93A4B8] font-dm mb-0.5">{computedStatus === 'ended' ? 'Status' : 'Time Left'}</div>
            <span className="font-mono text-sm text-[#93A4B8]">{computedStatus === 'ended' ? 'Ended' : drop.time_left || 'Ongoing'}</span>
          </div>
        )}
        {drop.blockchain && (
          <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-2.5">
            <div className="text-xs text-[#93A4B8] font-dm mb-0.5">Chain</div>
            <div className="font-dm text-xs text-[#E6F1FF]">{drop.blockchain}</div>
          </div>
        )}
        {(drop.daily_volume || 0) > 0 && (
          <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-2.5">
            <div className="text-xs text-[#93A4B8] font-dm mb-0.5">Daily Vol.</div>
            <div className="font-mono text-xs text-[#16C2FF]">${((drop.daily_volume || 0) / 1000).toFixed(0)}K</div>
          </div>
        )}
        {(drop.holder_count || 0) > 0 && (
          <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-2.5">
            <div className="text-xs text-[#93A4B8] font-dm mb-0.5">Holders</div>
            <div className="font-mono text-xs text-[#00E5A8]">{(drop.holder_count || 0).toLocaleString()}</div>
          </div>
        )}
        {drop.total_participants && (
          <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-2.5">
            <div className="text-xs text-[#93A4B8] font-dm mb-0.5">Participants</div>
            <div className="font-mono text-xs text-[#E6F1FF]">{drop.total_participants}</div>
          </div>
        )}
      </div>

      {/* Rating display on card */}
      {(drop as any).admin_rating && (
        <div className="flex items-center gap-1 mb-3">
          {[1,2,3,4,5].map(s => (
            <Star key={s} size={11} className={s <= Math.round((drop as any).admin_rating) ? 'text-[#F59E0B] fill-[#F59E0B]' : 'text-[#4A5568]'} />
          ))}
          <span className="text-[10px] text-[#93A4B8] ml-1">{((drop as any).admin_rating as number).toFixed(1)}</span>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        {drop.participation_type && (
          <span className="text-xs px-2 py-1 rounded-md bg-[rgba(22,194,255,0.08)] text-[#16C2FF] font-dm">{drop.participation_type}</span>
        )}
        {drop.category && (
          <span className="text-xs px-2 py-1 rounded-md bg-[rgba(255,255,255,0.04)] text-[#93A4B8] font-dm">{drop.category}</span>
        )}
      </div>

      {drop.telegram_link && (
        <a href={drop.telegram_link} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 text-xs text-[#93A4B8] hover:text-[#16C2FF] transition-colors mb-3"
          onClick={(e) => e.stopPropagation()}>
          <Send size={12} />
          Join Telegram Group
        </a>
      )}

      {computedStatus !== 'ended' ? (
        drop.join_url || drop.official_link ? (
          <a href={drop.join_url || drop.official_link} target="_blank" rel="noopener noreferrer"
            onClick={(e) => { e.stopPropagation(); onJoinAirdrop(drop.id, drop.project_name); }}
            className="w-full py-2.5 rounded-lg text-sm font-sora font-600 transition-all duration-200 flex items-center justify-center gap-2 btn-primary hover:opacity-90">
            {computedStatus === 'upcoming' ? 'Get Notified' : 'Join Airdrop'} <ExternalLink size={13} />
          </a>
        ) : (
          <button onClick={(e) => { e.stopPropagation(); onJoinAirdrop(drop.id, drop.project_name); }}
            className="w-full py-2.5 rounded-lg text-sm font-sora font-600 transition-all duration-200 flex items-center justify-center gap-2 btn-primary hover:opacity-90">
            {computedStatus === 'upcoming' ? 'Get Notified' : 'Join Airdrop'} <ArrowRight size={13} />
          </button>
        )
      ) : (
        <div className="w-full py-2.5 rounded-lg text-sm font-dm text-center text-[#93A4B8] bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)]">
          Campaign Ended
        </div>
      )}
    </div>
  );
}

// Best-effort parse of a reward string like "$500", "1,000 USDC", "2.5K", "$1.2M" → number (USD-ish)
function parseRewardValue(raw: string | undefined): number {
  if (!raw) return 0;
  const cleaned = String(raw).replace(/[$,\s]/g, '');
  const match = cleaned.match(/([\d.]+)\s*([kKmMbB])?/);
  if (!match) return 0;
  let value = parseFloat(match[1]);
  if (Number.isNaN(value)) return 0;
  const suffix = (match[2] || '').toLowerCase();
  if (suffix === 'k') value *= 1_000;
  else if (suffix === 'm') value *= 1_000_000;
  else if (suffix === 'b') value *= 1_000_000_000;
  return value;
}

function formatCompactUsd(n: number): string {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n)}`;
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${n}`;
}

export default function AirdropHubContent() {
  const [airdrops, setAirdrops] = useState<AirdropItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [heroStats, setHeroStats] = useState({ active: 0, totalRewards: 0, verified: 0, members: 0 });
  const [activeTab, setActiveTab] = useState('tab-all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBlockchain, setSelectedBlockchain] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState<'auto' | 'volume' | 'holders' | 'newest' | 'sort_order'>('auto');
  const [platformTelegram, setPlatformTelegram] = useState('');
  const [rocketName, setRocketName] = useState('AIDARAM Token');
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [adminSortMode, setAdminSortMode] = useState<'auto' | 'manual'>('auto');
  const [selectedAirdropId, setSelectedAirdropId] = useState<string | null>(null);
  // Advanced search
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [searchBlockchain, setSearchBlockchain] = useState('All');
  const [searchCategory, setSearchCategory] = useState('All');
  const [searchStatus, setSearchStatus] = useState('All');
  const [searchParticipation, setSearchParticipation] = useState('All');
  const { user } = useAuth();

  // If a detail page is selected, show it
  if (selectedAirdropId) {
    return <AirdropDetailPage airdropId={selectedAirdropId} onBack={() => setSelectedAirdropId(null)} />;
  }

  useEffect(() => {
    const fetchAirdrops = async () => {
      const supabase = createClient();
      const { data } = await supabase.from('airdrops').select('*').eq('is_active', true).order('sort_order', { ascending: true });
      const list = (data || []) as AirdropItem[];
      setAirdrops(list);

      // ── Dynamic hero stats (computed from real data) ──
      const active = list.filter(a => {
        const s = computeStatus(a);
        return s === 'active' || s === 'verified';
      }).length;
      const verified = list.filter(a => a.status === 'verified').length;
      const totalRewards = list.reduce((sum, a) => sum + parseRewardValue(a.reward_amount), 0);
      const { count: members } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true });
      setHeroStats({ active, totalRewards, verified, members: members || 0 });

      setLoading(false);
    };
    const fetchSettings = async () => {
      const supabase = createClient();
      const { data: telegramData } = await supabase.from('admin_settings').select('key, value').eq('key', 'platform_telegram_link').single();
      if (telegramData?.value) setPlatformTelegram(telegramData.value);
      const { data: rocketData } = await supabase.from('site_settings').select('setting_value').eq('setting_key', 'airdrop_section').single();
      if (rocketData?.setting_value?.rocket_name) setRocketName(rocketData.setting_value.rocket_name);
      // Load admin sort mode
      const { data: sortData } = await supabase.from('admin_settings').select('key, value').eq('key', 'airdrop_sort_mode').single();
      if (sortData?.value) setAdminSortMode(sortData.value as 'auto' | 'manual');
    };
    fetchAirdrops();
    fetchSettings();
  }, []);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, activeTab, selectedBlockchain, selectedCategory, searchBlockchain, searchCategory, searchStatus, searchParticipation, sortBy]);

  const handleJoinAirdrop = (airdropId: string, airdropName: string) => {
    logEvent('airdrop_join', { airdrop_id: airdropId, airdrop_name: airdropName }, user?.id);
    if (user?.email) {
      triggerAutomatedEmail({
        trigger_type: 'airdrop_join',
        user_id: user.id,
        email: user.email,
        full_name: user.user_metadata?.full_name || '',
        airdrop_name: airdropName,
      });
    }
  };

  const handleViewDetail = (airdropId: string) => {
    setSelectedAirdropId(airdropId);
  };

  const handleClaimProceed = () => {
    setClaimModalOpen(false);
    window.location.href = '/dashboard/claims';
  };

  const clearAdvancedSearch = () => {
    setSearchBlockchain('All');
    setSearchCategory('All');
    setSearchStatus('All');
    setSearchParticipation('All');
    setSearchQuery('');
  };

  const filtered = airdrops
    .filter((a) => {
      if (activeTab === 'tab-featured') return a.is_featured;
      if (activeTab === 'tab-active') return computeStatus(a) === 'active' || computeStatus(a) === 'verified';
      if (activeTab === 'tab-upcoming') return computeStatus(a) === 'upcoming';
      if (activeTab === 'tab-verified') return a.status === 'verified';
      return true;
    })
    .filter((a) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return a.project_name?.toLowerCase().includes(q) || a.token_symbol?.toLowerCase().includes(q) || a.description?.toLowerCase().includes(q);
      }
      return true;
    })
    .filter((a) => {
      const bc = searchBlockchain !== 'All' ? searchBlockchain : selectedBlockchain;
      return bc === 'All' || a.blockchain === bc;
    })
    .filter((a) => {
      const cat = searchCategory !== 'All' ? searchCategory : selectedCategory;
      return cat === 'All' || a.category === cat;
    })
    .filter((a) => searchStatus === 'All' || computeStatus(a) === searchStatus)
    .filter((a) => searchParticipation === 'All' || a.participation_type === searchParticipation)
    .sort((a, b) => {
      // If admin set manual mode, respect sort_order
      if (adminSortMode === 'manual') return (a.sort_order || 0) - (b.sort_order || 0);
      // Auto mode: sort by volume desc, then holders desc (daily algorithm)
      if (sortBy === 'auto' || sortBy === 'volume') {
        const vDiff = (b.daily_volume || 0) - (a.daily_volume || 0);
        if (vDiff !== 0) return vDiff;
        return (b.holder_count || 0) - (a.holder_count || 0);
      }
      if (sortBy === 'holders') return (b.holder_count || 0) - (a.holder_count || 0);
      if (sortBy === 'newest') return new Date(b.id).getTime() - new Date(a.id).getTime();
      return (a.sort_order || 0) - (b.sort_order || 0);
    });

  // Pagination
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.length > ITEMS_PER_PAGE
    ? filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
    : filtered;

  const tabs = [
    { id: 'tab-all', label: 'All Airdrops' },
    { id: 'tab-active', label: 'Active' },
    { id: 'tab-upcoming', label: 'Upcoming' },
    { id: 'tab-featured', label: 'Featured' },
    { id: 'tab-verified', label: 'Verified' },
  ];

  const hasAdvancedFilters = searchBlockchain !== 'All' || searchCategory !== 'All' || searchStatus !== 'All' || searchParticipation !== 'All';

  return (
    <div className="pt-16">
      {/* ── HERO: Dark Theme matching main website ── */}
      <section
        className="relative overflow-hidden py-16 lg:py-24"
        style={{
          background: 'linear-gradient(135deg, #07111F 0%, #0A1628 40%, #0D1B2A 100%)',
        }}
      >
        {/* Decorative glows */}
        <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full opacity-10 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #16C2FF 0%, transparent 70%)', transform: 'translate(-35%, -35%)' }} />
        <div className="absolute bottom-0 right-0 w-[450px] h-[450px] rounded-full opacity-8 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #8B5CF6 0%, transparent 70%)', transform: 'translate(35%, 35%)' }} />
        <div className="absolute top-1/3 right-1/4 w-72 h-72 rounded-full opacity-6 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #00E5A8 0%, transparent 70%)' }} />
        <div className="absolute bottom-1/3 left-1/4 w-56 h-56 rounded-full opacity-6 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #F59E0B 0%, transparent 70%)' }} />

        {/* Floating icons */}
        <div className="absolute top-16 right-16 opacity-20 pointer-events-none">
          <Star size={24} className="text-[#F59E0B] animate-spin" style={{ animationDuration: '8s' }} />
        </div>
        <div className="absolute top-32 left-20 opacity-20 pointer-events-none">
          <Zap size={20} className="text-[#16C2FF] animate-bounce" style={{ animationDuration: '2s' }} />
        </div>
        <div className="absolute bottom-24 right-24 opacity-20 pointer-events-none">
          <Rocket size={22} className="text-[#8B5CF6] animate-pulse" />
        </div>
        <div className="absolute top-1/2 left-8 opacity-15 pointer-events-none">
          <Star size={16} className="text-[#00E5A8] animate-pulse" style={{ animationDuration: '3s' }} />
        </div>

        <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 z-10">
          {/* Top badge */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-5"
              style={{
                background: 'rgba(22,194,255,0.08)',
                border: '1.5px solid rgba(22,194,255,0.2)',
                boxShadow: '0 2px 12px rgba(22,194,255,0.08)',
              }}>
              <Zap size={14} className="text-[#16C2FF]" />
              <span className="text-sm font-bold text-[#16C2FF] uppercase tracking-wider">🔥 AIDARAM Airdrop Launchpad</span>
            </div>
            <h1 className="font-black text-4xl sm:text-5xl lg:text-6xl text-[#E6F1FF] leading-tight mb-4">
              Discover &amp; Claim{' '}
              <span style={{
                background: 'linear-gradient(90deg, #16C2FF, #8B5CF6, #00E5A8)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                Airdrops
              </span>
            </h1>
            <p className="text-[#93A4B8] text-lg max-w-2xl mx-auto">
              Find verified airdrop campaigns, track live countdowns, and launch your token journey to the moon 🚀
            </p>
          </div>

          {/* Rocket + Stats side by side */}
          <div className="flex flex-col lg:flex-row items-center lg:items-start justify-center gap-10 lg:gap-16">
            {/* Rocket Launchpad */}
            <div className="flex-shrink-0">
              <RocketLaunchpad rocketName={rocketName} />
            </div>

            {/* Right side: stats + milestones info */}
            <div className="flex-1 max-w-lg">
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { label: 'Active Airdrops', value: loading ? '…' : `${heroStats.active}`, color: '#16C2FF', icon: '🚀' },
                  { label: 'Total Rewards', value: loading ? '…' : (heroStats.totalRewards > 0 ? formatCompactUsd(heroStats.totalRewards) : '—'), color: '#00E5A8', icon: '💰' },
                  { label: 'Verified Projects', value: loading ? '…' : `${heroStats.verified}`, color: '#8B5CF6', icon: '✅' },
                  { label: 'Community Members', value: loading ? '…' : formatCompact(heroStats.members), color: '#F59E0B', icon: '👥' },
                ].map((stat, i) => (
                  <div key={i} className="rounded-2xl p-4 text-center transition-all duration-200 hover:scale-105"
                    style={{
                      background: `rgba(${stat.color === '#16C2FF' ? '22,194,255' : stat.color === '#00E5A8' ? '0,229,168' : stat.color === '#8B5CF6' ? '139,92,246' : '245,158,11'},0.06)`,
                      border: `1.5px solid ${stat.color}25`,
                      boxShadow: `0 2px 12px ${stat.color}10`,
                    }}>
                    <div className="text-2xl mb-1">{stat.icon}</div>
                    <div className="font-black text-xl" style={{ color: stat.color }}>{stat.value}</div>
                    <div className="text-xs text-[#93A4B8] mt-0.5">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Milestone track legend */}
              <div className="rounded-2xl p-5"
                style={{
                  background: 'rgba(22,194,255,0.04)',
                  border: '1.5px solid rgba(22,194,255,0.15)',
                }}>
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp size={14} className="text-[#16C2FF]" />
                  <span className="text-sm font-bold text-[#E6F1FF]">Token Growth Milestones</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {SCALE_MILESTONES.map((m, i) => (
                    <span key={m} className="px-2.5 py-1 rounded-full text-xs font-black"
                      style={{
                        background: `${MILESTONE_COLORS[i]}18`,
                        color: MILESTONE_COLORS[i],
                        border: `1.5px solid ${MILESTONE_COLORS[i]}35`,
                      }}>
                      {m}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-[#93A4B8] mt-3 leading-relaxed">
                  Watch the rocket launch and track potential token growth milestones from 2X all the way to 5000X. DYOR — past performance does not guarantee future results.
                </p>
              </div>

              {/* Disclaimer */}
              <div className="mt-4 p-4 rounded-2xl"
                style={{ background: 'rgba(245,158,11,0.06)', border: '1.5px solid rgba(245,158,11,0.2)' }}>
                <p className="text-xs text-[#93A4B8] leading-relaxed">
                  <span className="text-[#F59E0B] font-bold">⚠️ Disclaimer:</span> AIDARAM lists airdrop campaigns submitted by third-party projects. Each project is responsible for its own token distribution and reward delivery. Always do your own research.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AIRDROP GRID ── */}
      <div className="bg-[#07111F] pb-16">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 pt-10">
          {/* Tabs */}
          <div className="flex items-center gap-2 flex-wrap mb-6 border-b border-[rgba(22,194,255,0.08)] pb-4">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-[rgba(22,194,255,0.15)] text-[#16C2FF] border border-[rgba(22,194,255,0.3)]'
                    : 'text-[#93A4B8] hover:text-[#E6F1FF] border border-transparent'
                }`}>
                {tab.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              {adminSortMode === 'auto' && (
                <span className="text-[10px] px-2 py-1 rounded-full bg-[rgba(0,229,168,0.08)] text-[#00E5A8] border border-[rgba(0,229,168,0.15)]">
                  📊 Auto-sorted by volume
                </span>
              )}
              {adminSortMode === 'manual' && (
                <span className="text-[10px] px-2 py-1 rounded-full bg-[rgba(245,158,11,0.08)] text-[#F59E0B] border border-[rgba(245,158,11,0.15)]">
                  ✋ Manually ordered
                </span>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#93A4B8]" />
              <input type="text" placeholder="Search airdrops by name, token, description..." value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full bg-[#0D1B2A] border border-[rgba(22,194,255,0.12)] rounded-lg pl-9 pr-4 py-2.5 text-sm text-[#E6F1FF] placeholder-[#93A4B8]/50 focus:outline-none focus:border-[rgba(22,194,255,0.4)]"
              />
            </div>
            <div className="flex gap-3 flex-wrap">
              <select value={selectedBlockchain} onChange={e => setSelectedBlockchain(e.target.value)}
                className="flex-1 sm:flex-none bg-[#0D1B2A] border border-[rgba(22,194,255,0.12)] rounded-lg px-3 py-2.5 text-sm text-[#E6F1FF] focus:outline-none focus:border-[rgba(22,194,255,0.4)] cursor-pointer">
                {blockchainFilters.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}
                className="flex-1 sm:flex-none bg-[#0D1B2A] border border-[rgba(22,194,255,0.12)] rounded-lg px-3 py-2.5 text-sm text-[#E6F1FF] focus:outline-none focus:border-[rgba(22,194,255,0.4)] cursor-pointer">
                {categoryFilters.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {adminSortMode === 'auto' && (
                <select value={sortBy} onChange={e => setSortBy(e.target.value as typeof sortBy)}
                  className="flex-1 sm:flex-none bg-[#0D1B2A] border border-[rgba(22,194,255,0.12)] rounded-lg px-3 py-2.5 text-sm text-[#E6F1FF] focus:outline-none focus:border-[rgba(22,194,255,0.4)] cursor-pointer">
                  <option value="auto">📊 Volume + Holders</option>
                  <option value="volume">💹 Max Volume</option>
                  <option value="holders">👥 Max Holders</option>
                  <option value="newest">🆕 Newest</option>
                  <option value="sort_order">🔢 Default</option>
                </select>
              )}
              <button
                onClick={() => setAdvancedOpen(!advancedOpen)}
                className={`flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all border ${
                  hasAdvancedFilters || advancedOpen
                    ? 'bg-[rgba(22,194,255,0.15)] text-[#16C2FF] border-[rgba(22,194,255,0.3)]'
                    : 'bg-[#0D1B2A] text-[#93A4B8] border-[rgba(22,194,255,0.12)] hover:text-[#E6F1FF]'
                }`}
              >
                <Search size={13} />
                Advanced
                {hasAdvancedFilters && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[#16C2FF] ml-1" />
                )}
              </button>
            </div>
          </div>

          {/* Advanced Search Panel */}
          {advancedOpen && (
            <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.12)] rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-[#16C2FF] uppercase tracking-wider">Advanced Search</span>
                {hasAdvancedFilters && (
                  <button onClick={clearAdvancedSearch} className="text-xs text-[#93A4B8] hover:text-red-400 transition-colors">
                    Clear all filters
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[10px] text-[#4A5568] mb-1 uppercase tracking-wider">Blockchain</label>
                  <select value={searchBlockchain} onChange={e => setSearchBlockchain(e.target.value)}
                    className="w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(22,194,255,0.1)] rounded-lg px-2 py-2 text-xs text-[#E6F1FF] focus:outline-none focus:border-[rgba(22,194,255,0.4)]">
                    {blockchainFilters.map(b => <option key={b} value={b} className="bg-[#0D1B2A]">{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-[#4A5568] mb-1 uppercase tracking-wider">Category</label>
                  <select value={searchCategory} onChange={e => setSearchCategory(e.target.value)}
                    className="w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(22,194,255,0.1)] rounded-lg px-2 py-2 text-xs text-[#E6F1FF] focus:outline-none focus:border-[rgba(22,194,255,0.4)]">
                    {categoryFilters.map(c => <option key={c} value={c} className="bg-[#0D1B2A]">{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-[#4A5568] mb-1 uppercase tracking-wider">Status</label>
                  <select value={searchStatus} onChange={e => setSearchStatus(e.target.value)}
                    className="w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(22,194,255,0.1)] rounded-lg px-2 py-2 text-xs text-[#E6F1FF] focus:outline-none focus:border-[rgba(22,194,255,0.4)]">
                    {['All', 'active', 'upcoming', 'ended', 'verified'].map(s => <option key={s} value={s} className="bg-[#0D1B2A]">{s === 'All' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-[#4A5568] mb-1 uppercase tracking-wider">Participation</label>
                  <select value={searchParticipation} onChange={e => setSearchParticipation(e.target.value)}
                    className="w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(22,194,255,0.1)] rounded-lg px-2 py-2 text-xs text-[#E6F1FF] focus:outline-none focus:border-[rgba(22,194,255,0.4)]">
                    {['All', 'Task-Based', 'Contribution', 'KYC Required', 'Testnet', 'Node Operation', 'Deposit-Based', 'Wallet Connect', 'Social'].map(p => (
                      <option key={p} value={p} className="bg-[#0D1B2A]">{p === 'All' ? 'All Types' : p}</option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-[10px] text-[#4A5568] mt-3">
                {filtered.length} airdrop{filtered.length !== 1 ? 's' : ''} match your filters
              </p>
            </div>
          )}

          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.08)] rounded-xl p-5 animate-pulse">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-[rgba(255,255,255,0.04)]" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-[rgba(255,255,255,0.04)] rounded w-3/4" />
                      <div className="h-2 bg-[rgba(255,255,255,0.04)] rounded w-1/2" />
                    </div>
                  </div>
                  <div className="space-y-2 mb-4">
                    <div className="h-2 bg-[rgba(255,255,255,0.04)] rounded" />
                    <div className="h-2 bg-[rgba(255,255,255,0.04)] rounded w-5/6" />
                  </div>
                  <div className="h-10 bg-[rgba(255,255,255,0.04)] rounded-lg" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <AlertTriangle size={40} className="text-[#93A4B8] mx-auto mb-4" />
              <p className="text-[#93A4B8]">No airdrops found matching your filters.</p>
              {hasAdvancedFilters && (
                <button onClick={clearAdvancedSearch} className="mt-3 text-sm text-[#16C2FF] hover:underline">Clear advanced filters</button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {paginated.map(drop => (
                  <AirdropCard key={drop.id} drop={drop} onJoinAirdrop={handleJoinAirdrop} onViewDetail={handleViewDetail} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 rounded-lg text-sm text-[#93A4B8] bg-[#0D1B2A] border border-[rgba(22,194,255,0.1)] hover:text-[#E6F1FF] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    ← Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                        currentPage === page
                          ? 'bg-[rgba(22,194,255,0.15)] text-[#16C2FF] border border-[rgba(22,194,255,0.3)]'
                          : 'text-[#93A4B8] bg-[#0D1B2A] border border-[rgba(22,194,255,0.08)] hover:text-[#E6F1FF]'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 rounded-lg text-sm text-[#93A4B8] bg-[#0D1B2A] border border-[rgba(22,194,255,0.1)] hover:text-[#E6F1FF] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    Next →
                  </button>
                  <span className="text-xs text-[#4A5568] ml-2">
                    Page {currentPage} of {totalPages} ({filtered.length} total)
                  </span>
                </div>
              )}
            </>
          )}

          {platformTelegram && (
            <div className="mt-12 text-center">
              <a href={platformTelegram} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[rgba(22,194,255,0.1)] border border-[rgba(22,194,255,0.25)] rounded-xl text-sm font-medium text-[#16C2FF] hover:bg-[rgba(22,194,255,0.15)] transition-colors">
                <Send size={14} />
                Join AIDARAM Telegram for Airdrop Alerts
              </a>
            </div>
          )}

          {/* DARAM Claim CTA */}
          <div className="mt-8 bg-gradient-to-r from-[#0D1B2A] to-[#112236] border border-[rgba(22,194,255,0.15)] rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-4">
            <div className="flex-1">
              <p className="text-sm font-sora font-bold text-[#E6F1FF] mb-1">Ready to Claim Your DARAM Tokens?</p>
              <p className="text-xs text-[#93A4B8]">Check which blockchain your tokens are on before claiming.</p>
            </div>
            <button
              onClick={() => setClaimModalOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#16C2FF] to-[#0EA5E9] text-[#07111F] font-semibold rounded-xl text-sm hover:opacity-90 transition-opacity flex-shrink-0"
            >
              <Zap size={14} />
              Claim DARAM Tokens
            </button>
          </div>
        </div>
      </div>

      <ClaimPreMessageModal
        isOpen={claimModalOpen}
        onClose={() => setClaimModalOpen(false)}
        onProceed={handleClaimProceed}
      />
    </div>
  );
}
